import React, { useCallback, useEffect, Suspense, useState } from "react";
import { LoadingOverlay, Button, Stack, Box, Container, Text, Group } from "@mantine/core";

import { IconPlus } from '@tabler/icons-react';

import { useAccountStore } from "../zustand/account";
import { useObjectStore } from "../zustand/objects";

import AppNavBar from "../components/AppNavBar";
import DeleteProjectModal from "../components/DeleteProjectModal";
import ProjectsTable from "../components/ProjectsTable";
import { getGuestProjectsAPI } from "../api";

import {
  editorModalShell,
  editorModalSecondaryButtonStyles,
  getStoryboardChromeTheme,
  pageLayoutProps,
} from "../styleFile.js";
import { SECTION_GAP, CHROME_HEADER_HEIGHT } from "../utils/uiChromeTokens";

const ProjectsPage = () => {
  const [guestProjects, setGuestProjects] = useState();
  const [loading, setLoading] = useState(true);

  const getGuestProjects = useCallback(() => {
    getGuestProjectsAPI().then((res) => setGuestProjects(res));
  }, []);

  const { projects, createNewProject, duplicateExistingProject, getUserProjects, deleteID, userStatus } =
    useAccountStore();
  const { loadObjects, getAIModels, objectsAreLoaded, viewportThemeOverride } = useObjectStore();
  const isDark = viewportThemeOverride === "dark";
  const sbTheme = getStoryboardChromeTheme(isDark);
  const newProjectButtonStyles = {
    ...editorModalSecondaryButtonStyles,
    root: {
      ...editorModalSecondaryButtonStyles.root,
      color: isDark ? "var(--mantine-color-gray-0)" : "#111827",
    },
    label: {
      ...editorModalSecondaryButtonStyles.label,
      color: isDark ? "var(--mantine-color-gray-0)" : "#111827",
    },
  };

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    document.body.setAttribute("data-editor-theme", isDark ? "dark" : "light");
    return () => {
      document.body.removeAttribute("data-editor-theme");
    };
  }, [isDark]);

  const setLoader = useCallback(async () => {
    await Promise.resolve(loadObjects());
    setLoading(false);
  }, [loadObjects]);

  const getProjects = useCallback(async () => {
    await Promise.resolve(getAIModels());
    await Promise.resolve(getUserProjects());
    await Promise.resolve(getGuestProjects());
  }, [getAIModels, getUserProjects, getGuestProjects]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await Promise.resolve(getProjects());
      if (cancelled) return;
      await Promise.resolve(setLoader());
    })();
    return () => {
      cancelled = true;
    };
  }, [getProjects, setLoader]);

  const handleSubmit = (e) => {
    setLoading(true);
    sendProject(e)
      .then(() =>
        setTimeout(() => {
          getProjects();
        }, 400)
      )
      .then(() => setLoader());
  };

  const handleCopySubmit = (e) => {
    setLoading(true);
    sendDuplicateProject(e)
      .then(() =>
        setTimeout(() => {
          getProjects();
        }, 400)
      )
      .then(() => setLoader());
  };

  async function sendProject(e) {
    let projectsLength = projects.length;
    createNewProject(`Project ${(projectsLength += 1)}`);
  }

  async function sendDuplicateProject(e) {
    duplicateExistingProject(e);
  }

  const isLimitedPlan = userStatus === "loggedIn-none" || userStatus === "loggedIn-canceled";

  const newProjectButton =
    projects &&
    (isLimitedPlan ? (
      projects.length < 1 ? (
        <Button
          leftSection={<IconPlus size={18} />}
          onClick={(e) => handleSubmit(e)}
          radius={4}
          styles={newProjectButtonStyles}
        >
          New project
        </Button>
      ) : (
        <Button
          size="md"
          radius={4}
          disabled
          variant="light"
          color="gray"
          styles={{
            root: {
              border: "1px dashed var(--mantine-color-default-border)",
              backgroundColor: "var(--mantine-color-default-hover)",
              color: "var(--mantine-color-dimmed)",
              opacity: 1,
            },
            label: { color: "var(--mantine-color-dimmed)" },
          }}
        >
          Upgrade for more projects
        </Button>
      )
    ) : (
      <Button
        leftSection={<IconPlus size={18} />}
        onClick={(e) => handleSubmit(e)}
        radius={4}
        styles={newProjectButtonStyles}
      >
        New project
      </Button>
    ));

  return (
    <Stack gap={0} style={{ minHeight: "100vh", width: "100%", backgroundColor: sbTheme.stackBg }}>
      {/* Note: secondary-outline-hover is the muted_fill from your Figma tokens */}
      <Suspense fallback={<div>Loading…</div>}>
        <DeleteProjectModal deleteID={deleteID} />
        <AppNavBar pageName="projects" />

        <Box
          style={{
            backgroundColor: sbTheme.mainBg,
            minHeight: `calc(100vh - ${CHROME_HEADER_HEIGHT}px)`,
            width: "100%",
            boxSizing: "border-box",
            paddingBottom: 48,
          }}
        >
          <Container
            {...pageLayoutProps}
            styles={{ root: { width: "100%" } }}
          >
            <Box style={{ position: "relative", minHeight: 200 }}>
              <LoadingOverlay
                visible={loading}
                zIndex={900}
                overlayProps={{ blur: 2, backgroundOpacity: 0.35 }}
                loaderProps={{ color: "gray", type: "oval" }}
              />

              <Stack gap={SECTION_GAP}>
                <Stack gap="lg">
                  <Group justify="space-between" align="flex-end" wrap="wrap" gap="md">
                    <Stack gap={4}>
                      <Text
                        style={{
                          ...editorModalShell.styles.title,
                          color: sbTheme.sectionLabelColor,
                          fontFamily: "Inter, sans-serif",
                        }}
                      >
                        Your creations
                      </Text>
                      <Text size="xs" maw={440} visibleFrom="sm" style={{ color: sbTheme.mutedTextColor }}>
                        Open the editor to build scenes, or duplicate an existing project.
                      </Text>
                    </Stack>
                    {newProjectButton}
                  </Group>

                  {!loading && projects && objectsAreLoaded && (
                    <ProjectsTable
                      handleCopySubmit={handleCopySubmit}
                      isGuest={false}
                      objectsAreLoaded={objectsAreLoaded}
                      projects={projects}
                    />
                  )}
                </Stack>

                <Stack gap="lg" pt={{ base: 20, md: SECTION_GAP }}>
                  <Stack gap={4}>
                    <Text
                      style={{
                        ...editorModalShell.styles.title,
                        color: sbTheme.sectionLabelColor,
                        fontFamily: "Inter, sans-serif",
                      }}
                    >
                      Creations by others
                    </Text>
                    <Text size="xs" maw={440} visibleFrom="sm" style={{ color: sbTheme.mutedTextColor }}>
                      Projects shared with you appear here.
                    </Text>
                  </Stack>
                  {!loading && projects && objectsAreLoaded && guestProjects != null && (
                    <ProjectsTable
                      isGuest
                      objectsAreLoaded={objectsAreLoaded}
                      projects={guestProjects}
                    />
                  )}
                </Stack>
              </Stack>
            </Box>
          </Container>
        </Box>
      </Suspense>
    </Stack>
  );
};

export default ProjectsPage;
